import React, { type FC } from 'react';
import clsx from 'clsx';
import { Button, FlexBox, Text } from '@is24at/react-ui-core';
import {
  ToggleField,
  TextAreaField,
  RadioButtonGroup,
  InputField,
  EmailField,
  PhoneField,
} from '@is24at/react-ui-core/Form';
import { useResponsive } from '@is24at/react-hooks';
import { FormSubmitStatus } from 'routes/Expose/modules/form';
import { getPattern, getRequired } from './utils';
import styles from './Form.scss';

type Props = {
  contactRequestConfiguration: Array<any>;
  companyName: string;
  formSubmitted: boolean;
  formSubmitStatus: FormSubmitStatus;
  isSafari?: boolean;
};

const radioOptions = [
  { value: 'Frau', label: 'Frau' },
  { value: 'Herr', label: 'Herr' },
  { value: 'Divers', label: 'Divers' },
];

const FormControls: FC<Props> = ({
  contactRequestConfiguration,
  companyName,
  formSubmitted,
  formSubmitStatus,
  isSafari = false,
}) => {
  const { isMobile } = useResponsive();

  return (
    <FlexBox direction="column" spacing={0} alignItems="normal">
      <Text className={styles.title} tag="h1" size="l">
        {companyName} freut sich über deine Nachricht!
      </Text>
      <FlexBox
        className={styles['control-container']}
        alignItems="normal"
        spacing={isMobile ? 0 : 16}
        fullWidth
      >
        <FlexBox direction="column" alignItems="normal" fullWidth>
          <FlexBox
            direction="column"
            alignItems="flex-start"
            spacing={2}
            wrap
            fullWidth
          >
            <Text bold>Nachricht</Text>
            <TextAreaField
              fieldProps={{
                'aria-label': 'Nachricht',
                name: 'messageBody',
                placeholder: 'Nachricht',
                required: true,
              }}
              containerProps={{
                className: clsx(styles['form-input'], {
                  [styles.safari]: isSafari,

                  [styles['with-address']]: getRequired(
                    'address',
                    contactRequestConfiguration,
                  ),
                }),
              }}
            />
          </FlexBox>
          {!isMobile && (
            <FlexBox className={styles.toggle} fullWidth>
              <Text>Ich bin Eigentümer:in einer Immobilie</Text>
              <ToggleField
                fieldProps={{
                  'aria-label': 'Hausbesitzer',
                  name: 'isHomeowner',
                }}
              />
            </FlexBox>
          )}
        </FlexBox>
        <FlexBox direction="column" spacing={24} alignItems="normal" fullWidth>
          <FlexBox
            className={styles['radio-group']}
            direction="column"
            alignItems="normal"
            spacing={8}
          >
            <Text bold>
              Anrede
              {getRequired('salutation', contactRequestConfiguration) && '*'}
            </Text>
            <FlexBox direction="row" spacing={16}>
              <RadioButtonGroup
                name="salutation"
                options={radioOptions}
                registerOptions={{
                  required:
                    getRequired('salutation', contactRequestConfiguration) &&
                    'Bitte wähle hier noch die passende Anrede.',
                }}
                labelProps={{
                  className: styles.salutation__label,
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox direction="column" alignItems="flex-start" spacing={2}>
            <Text bold>
              Vorname
              {getRequired('firstName', contactRequestConfiguration) && '*'}
            </Text>
            <InputField
              fieldProps={{
                'aria-label': 'Vorname',
                name: 'firstName',
                placeholder: 'Vorname',
                type: 'text',
                required: getRequired('firstName', contactRequestConfiguration),
              }}
              containerProps={{ className: styles['form-input'] }}
            />
          </FlexBox>
          <FlexBox direction="column" alignItems="flex-start" spacing={2}>
            <Text bold>
              Nachname
              {getRequired('lastName', contactRequestConfiguration) && '*'}
            </Text>
            <InputField
              fieldProps={{
                'aria-label': 'Nachname',
                name: 'lastName',
                placeholder: 'Nachname',
                type: 'text',
                required: getRequired('lastName', contactRequestConfiguration),
              }}
              containerProps={{ className: styles['form-input'] }}
            />
          </FlexBox>
          <FlexBox direction="column" alignItems="flex-start" spacing={2}>
            <Text bold>
              E-Mail{getRequired('email', contactRequestConfiguration) && '*'}
            </Text>
            <EmailField
              fieldProps={{
                'aria-label': 'E-Mail',
                name: 'email',
                placeholder: 'E-Mail',
                required: getRequired('email', contactRequestConfiguration),
              }}
              containerProps={{ className: styles['form-input'] }}
            />
          </FlexBox>
          <FlexBox direction="column" alignItems="flex-start" spacing={2}>
            <Text bold>
              Telefon
              {getRequired('phoneNumber', contactRequestConfiguration) && '*'}
            </Text>
            <PhoneField
              phonePattern={getPattern(
                'phoneNumber',
                contactRequestConfiguration,
              )}
              fieldProps={{
                'aria-label': 'Telefon',
                name: 'phoneNumber',
                placeholder: 'Telefon',
                inputMode: 'numeric',
                required: getRequired(
                  'phoneNumber',
                  contactRequestConfiguration,
                ),
              }}
              containerProps={{ className: styles['form-input'] }}
            />
          </FlexBox>
          {getRequired('zipcode', contactRequestConfiguration) && (
            <FlexBox direction="column" alignItems="flex-start" spacing={2}>
              <Text bold>PLZ*</Text>
              <InputField
                fieldProps={{
                  'aria-label': 'PLZ',
                  name: 'zipcode',
                  placeholder: 'PLZ',
                  inputMode: 'numeric',
                  type: 'text',
                  required: true,
                }}
                containerProps={{ className: styles['form-input'] }}
              />
            </FlexBox>
          )}
          {getRequired('city', contactRequestConfiguration) && (
            <FlexBox direction="column" alignItems="flex-start" spacing={2}>
              <Text bold>Ort*</Text>
              <InputField
                fieldProps={{
                  'aria-label': 'Ort',
                  name: 'city',
                  placeholder: 'Ort',
                  type: 'text',
                  required: true,
                }}
                containerProps={{ className: styles['form-input'] }}
              />
            </FlexBox>
          )}
          {getRequired('address', contactRequestConfiguration) && (
            <FlexBox direction="column" alignItems="flex-start" spacing={2}>
              <Text bold>Adresse*</Text>
              <InputField
                fieldProps={{
                  'aria-label': 'Adresse',
                  name: 'address',
                  placeholder: 'Straße / Hausnummer',
                  type: 'text',
                  required: true,
                }}
                containerProps={{ className: styles['form-input'] }}
              />
            </FlexBox>
          )}
          {isMobile && (
            <FlexBox className={styles.toggle} fullWidth>
              <Text>Ich bin Eigentümer:in einer Immobilie</Text>
              <ToggleField
                fieldProps={{
                  'aria-label': 'Hausbesitzer',
                  name: 'isHomeowner',
                }}
              />
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>
      <Button
        className={styles['submit-btn']}
        type="submit"
        disabled={
          (formSubmitted && formSubmitStatus === FormSubmitStatus.SUCCESS) ||
          formSubmitStatus === FormSubmitStatus.REQUESTED
        }
      >
        Nachricht senden
      </Button>
    </FlexBox>
  );
};

export default FormControls;
